import "./SearchBox.css";
import React, { useEffect, useState, useRef, useMemo } from "react";
import { InputText } from "primereact/inputtext";
import { Dropdown } from "primereact/dropdown";
import { Calendar } from "primereact/calendar";
import { useForm, Controller } from "react-hook-form";
import { Button } from "primereact/button";
import { useModal } from "../../hooks/ModalContext";
import useFocusableExternalSerachScreenElements from "./useFocusableExternalSerachScreenElements";
import { useLoader } from "../../hooks/LoaderContext";
import SearchBoxCalendarDate from "./SearchBoxCalendarDate";

const isDisableStatusDropdown = (
  isModalVisible = false,
  statusDropdownFieldName = "",
  name = ""
) => {
  return (
    (isModalVisible || window?.self?.name === "external-lookup-iframe") &&
    statusDropdownFieldName === name
  );
};

const SearchBox = ({
  formFields,
  dropdownValues,
  paramsData,
  updataParamData,
  disabledDropdowns = {}
}) => {
  const { register, control, handleSubmit, setValue, reset, getValues } =
    useForm({
      defaultValues: paramsData,
    });

  const { isModalVisible } = useModal();
  const { isLoaderEnabled } = useLoader();
  useFocusableExternalSerachScreenElements();

  const formRef  = useRef();
  const inputRefs = useRef([]);

  const [focusedInput, setFocusedInput] = useState(null);

  const onSubmit = (data) => {
    console.log(data);
    const updatedObj = { ...data };
    for (let key in updatedObj) {
      if (updatedObj[key] === undefined) {
        updatedObj[key] = "";
      }
    }
    data = updatedObj;
    updataParamData(data);
  };

  useEffect(() => {
    if (!isLoaderEnabled) {
      const timeout = setTimeout(() => {
        const firstInput = inputRefs?.current?.filter(Boolean)?.[0]
        if (firstInput) {
          firstInput.focus();
          firstInput.select?.();
        }
      }, 100);
      return () => {
        clearTimeout(timeout);
      };
    }
  }, [isLoaderEnabled]);

  const handleKeyDown = (event) => {
    if (event.key === "Enter") {
      event.preventDefault();
      handleSubmit(onSubmit)();
    }
  };

  const handleFocus = (inputName) => {
    setFocusedInput(inputName);
  };

  const getDate = (dateString) => (dateString ? new Date(dateString) : null);

  const getFieldLabel = (fieldValue, options) => {
    if (options !== undefined) {
      const filteredValues = options.filter(({ value }) => {
        return value === fieldValue;
      });
      return filteredValues.length ? filteredValues[0].label : "";
    }
  };

  useEffect(() => {
    // Set default values when paramsData changes
    if (paramsData) {
      reset(paramsData);
    }
  }, [paramsData, reset]);



  const statusDropdownFieldName = useMemo(
    () => formFields?.find((field) => field?.label === "Status")?.name || "",
    [formFields]
  );

  useEffect(() => {
    const handleKeyDown = (event) => {
      const { key } = event;
      if (statusDropdownFieldName && key === "F8") {
        event.preventDefault();
        const targetDropdown = formRef.current?.querySelector(
          `#${statusDropdownFieldName} input`
        );
        targetDropdown?.focus();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);

  return (
    <>
      <div className="search_box_container">
        <form
          className="search_form"
          autoComplete="off"
          aria-autocomplete="none"
          ref={formRef}
        >
          <div className="row">
            {formFields.map((formField, index) => {
              const {
                name,
                type,
                label,
                maxLength,
                fieldLabel,
                fieldNature,
                fieldHandler,
              } = formField;
              return (
                <div className="col col-20 no-label" key={formField.name}>
                  {type === "text" ? (
                    <>
                      <div className="input_label_container">
                        <label htmlFor={name} className="search_label">
                          {label}
                        </label>
                      </div>

                      <Controller
                        name={name}
                        control={control}
                        render={({ field }) => {
                          if (fieldNature === "readOnly") {
                            return <span id={name}>{field.value}</span>;
                          } else {
                            const { ref, ...rest } = register(name);
                            return (
                              <InputText
                                id={name}
                                {...rest}
                                onChange={(e) =>
                                  setValue(
                                    e.target.name,
                                    e.target.value.toUpperCase()
                                  )
                                }
                                ref={(el) => {
                                  ref(el);
                                  inputRefs.current[index] = el;
                                }}
                                maxLength={maxLength}
                                onKeyDown={handleKeyDown}
                                onFocus={() => handleFocus(name)}
                                className={
                                  focusedInput === name
                                    ? "p-inputtext-highlight"
                                    : ""
                                }
                                style={{ textTransform: "uppercase" }}
                                aria-autocomplete="none"
                                autoComplete="off"
                              />
                            );
                          }
                        }}
                      />
                    </>
                  ) : type === "dropdown" ? (
                    <>
                      <div className="input_label_container">
                        <label htmlFor={name} className="search_label">
                          {label}
                        </label>
                      </div>
                      <Controller
                        name={name}
                        control={control}
                        render={({ field }) => {
                          if (fieldNature === "readOnly") {
                            return (
                              <span id={name}>
                                {getFieldLabel(
                                  field.value,
                                  dropdownValues && dropdownValues[fieldLabel]
                                )}
                              </span>
                            );
                          } else {
                            return (
                              <Dropdown
                                id={name}
                                value={field.value}
                                options={
                                  (dropdownValues &&
                                    dropdownValues[fieldLabel]) ||
                                  []
                                }
                                optionLabel="label"
                                placeholder={label}
                                ref={(el) => {
                                  inputRefs.current[index] = el;
                                }}
                                onChange={(e) => {
                                  field.onChange(e.value);
                                  if (e.value !== field.value) {
                                    handleSubmit(onSubmit)();
                                  }
                                }}
                                onFocus={() => handleFocus(name)}
                                className={
                                  focusedInput === name
                                    ? "p-dropdown-highlight"
                                    : ""
                                }
                                {...((disabledDropdowns?.[name] ||
                                  isDisableStatusDropdown(
                                    isModalVisible,
                                    statusDropdownFieldName,
                                    name
                                  )) && {
                                  dropdownIcon: <></>,
                                  disabled: true,
                                  pt: {
                                    root: {
                                      style: {
                                        opacity: 1,
                                        border: "none",
                                        background: "transparent",
                                      },
                                    },
                                    trigger: {
                                      style: {
                                        display: "none",
                                      },
                                    },
                                    input: { style: { paddingLeft: 1 } },
                                  },
                                })}
                              />
                            );
                          }
                        }}
                      />
                    </>
                  ) : type === "date" ? (
                    <>
                      <div className="input_label_container">
                        <label htmlFor={name} className="search_label">
                          {label}
                        </label>
                      </div>
                      <Controller
                        name={name}
                        control={control}
                        render={({ field }) => {
                          if (fieldNature === "readOnly") {
                            return <span id={name}>{field.value}</span>;
                          } else {
                            return (
                              <SearchBoxCalendarDate
                                name={name}
                                getRef={(el) => {
                                  inputRefs.current[index] = el;
                                }}
                                onChange={field.onChange}
                                handleSubmit={handleSubmit(onSubmit)}
                                defaultValue={paramsData?.[name] || ""}
                              />
                            );
                          }
                        }}
                      />
                    </>
                  ) : type === "link" ? (
                    <>
                      <div className="input_label_container">
                        <label htmlFor={name} className="search_label">
                          {label}
                        </label>
                      </div>
                      <Controller
                        name={name}
                        control={control}
                        render={({ field }) => {
                          const formValues = getValues();
                          return (
                            <Button
                              label={field.value}
                              link
                              onClick={(e) => {
                                e.preventDefault();
                                fieldHandler(formValues);
                              }}
                            />
                          );
                        }}
                      />
                    </>
                  ) : (
                    <></>
                  )}
                </div>
              );
            })}
          </div>
        </form>
      </div>
    </>
  );
};

export default SearchBox;